import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';

import { FieldCapacity, Field } from '@app/_models/facilities';
import { SessionService } from '../session.service';
import {
  Tournament,
  TournamentTeam,
  SignInTeamRequest,
  Team,
  Fixture,
  PositionList,
  Matchset,
  Inscription,
} from '@app/_models/tournament.model';
import { Dictionary, PaginatedList } from '@app/_models/shared.model';

@Injectable({ providedIn: 'root' })
export class TournamentsService {
  constructor(
    private httpClient: HttpClient,
    private sessionService: SessionService
  ) { }

  createTournament(tournament: FormData): Observable<Tournament> {
    tournament.append('user', this.sessionService.currentUser.user.toString());
    tournament.append(
      'facility',
      this.sessionService.currentFacility.id.toString()
    );
    const url = `${environment.apiUrl}/tournaments/`;
    return this.httpClient.post<Tournament>(url, tournament);
  }

  getTournament(id: number): Observable<Tournament> {
    const url = `${environment.apiUrl}/tournaments/${id}/`;
    return this.httpClient.get<Tournament>(url);
  }

  getTournamentsStates(): Observable<Dictionary[]> {
    const url = `${environment.apiUrl}/tournaments/status/`;
    return this.httpClient.get<Dictionary[]>(url);
  }

  createTeam(team: Team): Observable<Team> {
    const url = `${environment.apiUrl}/tournaments_teams/`;
    return this.httpClient.post<Team>(url, team);
  }

  getFieldCapacities(): Observable<FieldCapacity[]> {
    const url = `${environment.apiUrl}/fieldCapacities/`;
    return this.httpClient.get<FieldCapacity[]>(url);
  }

  getFieldsByCapacity(id: number): Observable<Field[]> {
    const url = `${environment.apiUrl}/fieldCapacities/${id}/fields/`;
    return this.httpClient.get<Field[]>(url);
  }

  signInTeam(tournamentId: number, signInRequest: SignInTeamRequest): Observable<Inscription> {
    const url = `${environment.apiUrl}/tournaments/${tournamentId}/inscriptions/`;
    let params: Inscription = {
      ...signInRequest,
      facility: this.sessionService.currentFacility.id,
      user: this.sessionService.currentUser.user
    };
    return this.httpClient.post<Inscription>(url, params);
  }

  confirmTeamForTournament(tournamentId, inscriptionId): Observable<Inscription> {
    const url = `${environment.apiUrl}/tournaments/${tournamentId}/inscriptions/${inscriptionId}/`;
    return this.httpClient.patch<Inscription>(url, {
      status: 1
    });
  }

  getTeamsPaymentStates(): Observable<Dictionary[]> {
    const url = `${environment.apiUrl}/tournaments/inscriptions/status/`;
    return this.httpClient.get<Dictionary[]>(url);
  }



  createTournamentFixture(id: number): Observable<Fixture> {
    const url = `${environment.apiUrl}/tournaments/${id}/fixture/create/`;
    const params = {
      user: this.sessionService.currentUser.user,
      facility: this.sessionService.currentFacility.id,
    };
    return this.httpClient.post<Fixture>(url, params);
  }

  loadMatchScore(tournamentId, fixtureId, roundId, matchId, scores) {
    const url = `${environment.apiUrl}/tournaments/${tournamentId}/fixtures/${fixtureId}/rounds/${roundId}/matches/${matchId}/`;
    return this.httpClient.patch<Tournament>(url, { ...scores, user: this.sessionService.currentUser.user, facility: this.sessionService.currentFacility.id });
  }

  publishTournament(id) {
    const url = `${environment.apiUrl}/tournaments/${id}/`;
    return this.httpClient.patch<Tournament>(url, { state: 1 });
  }

  getTournamentFixture(id: number): Observable<Fixture[]> {
    const url = `${environment.apiUrl}/tournaments/${id}/fixtures/`;
    return this.httpClient.get<Fixture[]>(url);
  }

  getTournamentTeams(): Observable<TournamentTeam[]> {
    const url = `${environment.apiUrl}/tournaments_teams/`;
    return this.httpClient.get<TournamentTeam[]>(url);
  }

  getTournamentMatchesByRoundId(tournamentId, fixtureId, roundId): Observable<Matchset[]> {
    const url = `${environment.apiUrl}/tournaments/${tournamentId}/fixtures/${fixtureId}/rounds/${roundId}/matches/`;
    return this.httpClient.get<Matchset[]>(url);
  }

  getTournamentPositions(id: number): Observable<PositionList> {
    const url = `${environment.apiUrl}/tournaments/${id}/tables/`;
    return this.httpClient.get<PositionList>(url);
  }

  findTournaments(
    pageNumber,
    pageSize,
    status: number
  ): Observable<PaginatedList> {
    const url = `${environment.apiUrl}/tournaments/`;
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    if (status && status !== -1) {
      params.set('status', status.toString());
    }
    return this.httpClient.get<PaginatedList>(url, { params });
  }

  findTournamentTeams(
    pageNumber,
    pageSize,
    id: number
  ): Observable<PaginatedList> {
    const url = `${environment.apiUrl}/tournaments/${id}/inscriptions`;
    const params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    return this.httpClient.get<PaginatedList>(url, { params });
  }
}
